/**
 * Module pilote — Staff / Cockpit CFO
 * 20 slides : finance avancée, recouvrement, KPIs
 */
const moduleStaffCockpitCFO = {
  id: 'staff-cockpit-cfo',
  population: 'staff',
  order: 5,
  category: 'Finance & Pilotage',
  title: 'Cockpit CFO',
  shortTitle: 'Cockpit CFO',
  subtitle: 'Piloter la santé financière de Baobab Connect au quotidien',
  icon: 'DollarSign',
  duration: '30 min',
  level: 'Intermédiaire → Avancé',
  audience: 'Direction financière, contrôleurs de gestion',
  pdfUrl: '/api/uploads/docs/training/staff-cockpit-cfo.pdf',
  pptxUrl: '/api/uploads/docs/training/staff-cockpit-cfo.pptx',
  slides: [
    {
      type: 'cover',
      eyebrow: 'Module 05 — Staff',
      title: 'Cockpit CFO',
      subtitle: 'Pilotage financier temps réel : DSO, recouvrement, alertes & objectifs',
      icon: 'DollarSign',
      duration: '30 min',
      audience: 'CFO, comptables, contrôleurs',
      level: 'Intermédiaire → Avancé',
    },
    {
      type: 'intro',
      title: 'Objectifs du module',
      intro: "Le Cockpit CFO centralise tous les indicateurs financiers stratégiques. À la fin de ce module, vous saurez l'utiliser pour piloter la trésorerie au quotidien.",
      objectives: [
        'Lire et interpréter les 8 KPIs financiers principaux (CA, DSO, marge…)',
        'Identifier les factures à risque et lancer les relances',
        'Suivre la progression vs objectifs mensuels/trimestriels/annuels',
        'Auditer les divergences statut/facturation et les corriger',
        'Exporter les rapports CFO hebdomadaires automatisés',
        'Activer les alertes WhatsApp/email en cas de seuil critique',
      ],
    },
    {
      type: 'screenshot',
      eyebrow: 'Vue d\'ensemble',
      title: 'Anatomie du Cockpit CFO',
      callouts: [
        { label: 'Bandeau alertes', text: 'Incohérences détectées (factures sans opération, doublons, divergences statut/financier).' },
        { label: 'KPIs Stratégiques', text: 'Croissance MoM/YoY, ARPU, Pipeline, Concentration Top 10 clients.' },
        { label: 'Objectifs Business', text: 'CA mensuel/trim/annuel vs cibles, MRR contractuel, recouvrement.' },
        { label: 'Drill-down', text: 'Cliquez sur un KPI pour accéder aux opérations filtrées sous-jacentes.' },
      ],
    },
    {
      type: 'concept',
      eyebrow: 'Vocabulaire financier',
      icon: 'BookOpen',
      title: 'Les 8 KPIs clés à connaître',
      lede: 'Ces KPIs sont calculés en temps réel à partir des opérations et factures de la base.',
      bullets: [
        '📈 Croissance MoM / YoY : CA du mois vs mois précédent / année précédente.',
        '💰 ARPU : revenu moyen par client actif sur la période.',
        '🎯 Taux de conversion : devis acceptés / devis créés.',
        '🚦 Taux d\'annulation : opérations annulées / opérations totales.',
        '🔁 Taux de rétention : clients récurrents / clients actifs.',
        '⚠️ Concentration Top 10 : % CA réalisé par les 10 plus gros clients (alerte si > 50 %).',
        '⏱️ DSO (Days Sales Outstanding) : délai moyen d\'encaissement des factures.',
        '💸 Pipeline : valeur cumulée des devis en attente d\'acceptation.',
      ],
      note: 'Un KPI qui passe au rouge déclenche automatiquement une alerte CFO hebdomadaire.',
    },
    {
      type: 'steps',
      eyebrow: 'Mode opératoire',
      title: 'Filtrer les KPIs par période',
      steps: [
        { title: 'Cliquer sur le sélecteur de période', detail: 'En haut à droite : 7j / 30j / 90j / 12 mois / personnalisé.' },
        { title: 'Choisir une plage personnalisée si besoin', detail: 'Sélecteur date min/max avec preset (Mois en cours, T1, YTD…).' },
        { title: 'Appliquer', detail: 'Tous les KPIs et graphiques se rechargent en < 2 secondes.' },
        { title: 'Comparer avec période précédente', detail: 'Toggle « vs N-1 » pour ajouter les courbes comparatives.' },
      ],
    },
    {
      type: 'screenshot',
      eyebrow: 'Onglet Recouvrement',
      title: 'Suivre l\'encaissement client par client',
      callouts: [
        { label: 'DSO global', text: 'Délai moyen d\'encaissement sur la période — objectif < 45j.' },
        { label: 'Vieillissement créances', text: 'Tableau 0-30j / 31-60j / 61-90j / > 90j par client.' },
        { label: 'Top débiteurs', text: 'Liste des 10 clients avec l\'encours le plus élevé.' },
        { label: 'Actions de relance', text: 'Envoyer relance email, générer relevé PDF, lancer procédure légale.' },
      ],
    },
    {
      type: 'steps',
      eyebrow: 'Workflow critique',
      title: 'Lancer une campagne de relance',
      steps: [
        { title: 'Ouvrir l\'onglet Recouvrement', detail: 'Sous-onglet du Cockpit CFO.' },
        { title: 'Sélectionner les factures en retard', detail: 'Filtre Statut financier = En retard, période > 30j.' },
        { title: 'Bouton « Relancer en masse »', detail: 'Multi-sélection puis action groupée.' },
        { title: 'Personnaliser le message', detail: 'Template par défaut FR/EN, variables {clientName}, {amount}, {dueDate}.' },
        { title: 'Choisir le canal', detail: 'Email, WhatsApp (si configuré), SMS.' },
        { title: 'Tracer dans l\'historique', detail: 'Chaque relance est journalisée et visible sur la fiche client.' },
      ],
    },
    {
      type: 'concept',
      eyebrow: 'Conformité OHADA',
      icon: 'Shield',
      title: 'Audit factures : pourquoi & comment',
      lede: 'La page Audit Factures (accessible depuis la sidebar) détecte automatiquement les anomalies fiscales et comptables.',
      bullets: [
        'Factures « Inconnu » : nom de société manquant → backfill automatique.',
        'Factures legacy (N°00xxxxxx) : ancien format → régénération en FACT-YYYY-MM-seq.',
        'Dates désalignées : facture émise après la date d\'opération réelle.',
        'Divergences statut/facturation : opération terminée sans facture, ou inverse.',
        'Doublons : 2 factures pour la même opération.',
      ],
      note: 'Toutes ces anomalies sont auto-corrigeables via les boutons « Backfill » / « Régénérer » avec sélection partielle.',
    },
    {
      type: 'steps',
      eyebrow: 'Procédure',
      title: 'Régénérer les factures legacy en bulk',
      steps: [
        { title: 'Ouvrir Audit Factures depuis la sidebar', detail: 'Section « Direction » ou « Finance ».' },
        { title: 'Section « Factures legacy & dates désalignées »', detail: 'Liste filtrable avec compteur en temps réel.' },
        { title: 'Cocher les factures à traiter', detail: 'Sélection partielle pour traiter par lot (recommandé).' },
        { title: 'Bouton « Régénérer la sélection »', detail: 'Confirme avec récapitulatif (X factures, ancienne réf → nouvelle).' },
        { title: 'Vérifier les KPIs après', detail: 'Le dashboard CFO se met à jour automatiquement.' },
      ],
    },
    {
      type: 'screenshot',
      eyebrow: 'Objectifs Business',
      title: 'Suivre les objectifs vs réalisé',
      callouts: [
        { label: 'CA Mensuel', text: 'Jauge % d\'atteinte + montant restant à réaliser.' },
        { label: 'CA Trimestriel', text: 'Vue agrégée glissante sur 3 mois.' },
        { label: 'CA Annuel', text: 'YTD vs objectif annuel, projection fin d\'année.' },
        { label: 'Statuts colorés', text: 'Vert = Atteint, Ambre = En bonne voie, Rouge = Attention.' },
      ],
    },
    {
      type: 'concept',
      eyebrow: 'Paramétrage',
      icon: 'Target',
      title: 'Définir vos objectifs Business',
      lede: 'Les cibles sont configurables par la direction depuis le module Configuration.',
      bullets: [
        'Objectif CA mensuel : montant fixe ou progression % vs N-1.',
        'Objectif opérations / mois : volumétrie cible.',
        'Objectif recouvrement : taux d\'encaissement attendu (ex: 85 %).',
        'Objectif DSO : seuil maximal en jours (ex: ≤ 45j).',
        'Objectif nouveaux clients : nombre de signatures par mois.',
        'Tous ces paramètres alimentent les graphiques et alertes du Cockpit.',
      ],
      note: 'Modifier ces cibles en cours de période recalcule automatiquement le % d\'atteinte.',
    },
    {
      type: 'comparison',
      title: 'Pilotage CFO : bonnes pratiques vs pièges',
      do: [
        'Consulter le Cockpit CFO quotidiennement (5 min suffisent)',
        'Programmer un rituel hebdomadaire de revue d\'alertes',
        'Cliquer sur les KPIs pour drill-down et confirmer la cause',
        'Activer les alertes WhatsApp/email pour seuils critiques',
        'Exporter le rapport hebdomadaire automatique tous les lundis',
      ],
      dont: [
        'Ne se fier qu\'au CA brut sans regarder marge et DSO',
        'Ignorer les alertes « Concentration Top 10 > 50 % »',
        'Laisser des factures legacy non régénérées (risque OHADA)',
        'Modifier les objectifs en cours de période sans communication',
        'Exporter manuellement chaque semaine (utilisez l\'envoi auto)',
      ],
    },
    {
      type: 'screenshot',
      eyebrow: 'Alertes & notifications',
      title: 'Configurer les alertes CFO automatiques',
      callouts: [
        { label: 'Email hebdomadaire', text: 'Récap chaque lundi 8h avec PDF du rapport complet.' },
        { label: 'WhatsApp Direction', text: 'Push instantané si DSO > seuil ou concentration > 50 %.' },
        { label: 'Slack interne', text: 'Channel #finance-alerts pour l\'équipe comptabilité.' },
        { label: 'Seuils configurables', text: 'Chaque alerte a son propre déclencheur ajustable.' },
      ],
    },
    {
      type: 'quiz',
      title: 'Quiz #1',
      question: 'Le KPI « Concentration Top 10 » est passé à 58 %. Que faut-il faire en priorité ?',
      options: [
        'Rien, c\'est juste un indicateur informatif',
        'Augmenter immédiatement le prix pour ces clients',
        'Lancer une action commerciale pour diversifier la base client (objectif < 50 %)',
        'Supprimer ces clients pour réduire le pourcentage',
      ],
      answerIndex: 2,
      explanation: 'Une concentration > 50 % crée un risque business majeur : la perte d\'un seul client impacte fortement le CA. La bonne réponse est d\'élargir la base, pas de réduire les gros comptes.',
    },
    {
      type: 'steps',
      eyebrow: 'Cas réel',
      title: 'Procédure mensuelle de clôture',
      steps: [
        { title: 'J-2 : Lancer l\'audit factures', detail: 'Backfill companyName + régénération legacy si besoin.' },
        { title: 'J-1 : Vérifier divergences statut/financier', detail: 'Toutes les ops terminées doivent avoir leur facture.' },
        { title: 'J-1 : Lancer la campagne de relance', detail: 'Sur factures > 30j en retard.' },
        { title: 'J : Exporter le rapport CFO mensuel', detail: 'PDF + CSV avec tous les KPIs + objectifs.' },
        { title: 'J+1 : Présentation Direction', detail: 'Le PDF est prêt à être envoyé/imprimé.' },
        { title: 'J+2 : Ajuster les objectifs du mois suivant', detail: 'Depuis Configuration → Objectifs Business.' },
      ],
    },
    {
      type: 'quiz',
      title: 'Quiz #2',
      question: 'Quelle action lance la régénération du numéro d\'une facture legacy (N°00xxxxxx → FACT-YYYY-MM-seq) ?',
      options: [
        'Modifier manuellement chaque facture',
        'Cocher la facture dans Audit Factures puis « Régénérer la sélection »',
        'Supprimer la facture et la recréer',
        'Demander au support technique',
      ],
      answerIndex: 1,
      explanation: 'L\'audit propose une sélection partielle (checkboxes) puis un bouton de régénération en masse. Le numéro est recalculé à partir de la date de l\'opération, garantissant la conformité OHADA.',
    },
    {
      type: 'checklist',
      title: 'Checklist de la revue CFO hebdomadaire',
      items: [
        'Lire les alertes en bandeau (incohérences détectées)',
        'Vérifier les 8 KPIs stratégiques vs N-1',
        'Contrôler la jauge des objectifs (Mensuel / Trim / Annuel)',
        'Onglet Recouvrement : examiner les Top 10 débiteurs',
        'Lancer une relance sur les retards > 30 jours',
        'Audit Factures : traiter les anomalies en attente',
        'Exporter le rapport CFO et l\'archiver',
        'Communiquer les chiffres clés à la direction',
      ],
    },
    {
      type: 'takeaways',
      title: 'Les 5 réflexes CFO à retenir',
      items: [
        'Consulter le Cockpit chaque matin (5 min)',
        'Drill-down sur chaque KPI rouge pour identifier la cause',
        'Audit Factures = conformité OHADA + données KPI fiables',
        'Alertes WhatsApp/email indispensables pour pilotage proactif',
        'Rituel hebdomadaire de relance pour DSO maîtrisé',
      ],
    },
    {
      type: 'screenshot',
      eyebrow: 'Liens & ressources',
      title: 'Aller plus loin',
      callouts: [
        { label: 'Module Recouvrement', text: 'Procédures détaillées de relance multi-canal.' },
        { label: 'Module Audit Factures', text: 'Maîtriser toutes les anomalies détectables.' },
        { label: 'Module Configuration', text: 'Paramétrer objectifs, alertes, seuils.' },
        { label: 'Module Rapports', text: 'Exports personnalisés et envois programmés.' },
        { label: 'Module Profitabilité', text: 'Analyse de marge par client et par opération.' },
      ],
    },
    {
      type: 'cta',
      title: 'Module CFO terminé !',
      subtitle: 'Vous maîtrisez le pilotage financier de Baobab Connect. Mettez en pratique dès aujourd\'hui.',
      actions: [
        { label: 'Ouvrir le Cockpit CFO', href: '/admin/finance/cockpit' },
        { label: 'Audit factures', href: '/admin/audit-factures' },
        { label: 'Module suivant', href: '/docs/admin/training' },
      ],
    },
  ],
};

export default moduleStaffCockpitCFO;
